import { Box, Button, ButtonSize, Icon, IconSize, Text } from '@bechtel/gds';
import { ToolCatalog } from '@bechtel/ht-types';
import { ActionContent } from '~/components/action-content';
import { EditCountField } from '~/components/molecules/edit-count-field';
import { ToolOrderCatalogItem } from '~/views/reservations/types';
import { CartHeaderStyled, EditNonCatalogButtonStyled, ToolCartStyled, ToolCountStyled } from './styled';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { CartHeader } from '~/components/cart-header';
//@ts-ignore
import { ReactComponent as EmptyStateBox } from '~/images/empty-state-box.svg';
import { BoxWithBorderStyled } from '~/styles';
import { RemoveCartItemStyled } from '~/views/reservations/create/my-cart/styled';
import { CatalogInfo } from '../catalog-info';
import { ImageFallback } from '../image-fallback';
import RestrictedLimitComponent from '../molecules/restricted-limit';
import { NonCatalogImage, NonCatalogInfo } from '../non-catalog/list-item';

interface ToolCartProps {
  toolOrderCatalogItems: ToolOrderCatalogItem[];
  onToolCartItemEdit: (cartItem?: ToolCatalog, quantity?: number) => void;
  onNonCatalogItemEdit: (cartItem: ToolOrderCatalogItem, quantity: number, onlyQuantityEdit: boolean) => void;
  onToolCartSave: (showCart: boolean) => void;
  onToolCartItemRemove: (id: string, isNonCatalog: boolean) => void;
  hasCartChanges: boolean;
  onToggleShowCart?: () => void;
}

export const ToolCart: React.FC<ToolCartProps> = ({
  toolOrderCatalogItems,
  onToolCartItemEdit,
  onNonCatalogItemEdit,
  onToolCartSave,
  hasCartChanges,
  onToggleShowCart,
  onToolCartItemRemove,
}) => {
  const totalItems = toolOrderCatalogItems?.reduce((acc, item) => acc + item.quantity, 0);
  return (
    <ToolCartStyled>
      <Box flexDirection={{ sm: 'column' }}>
        <BoxWithBorderStyled
          borderBottomColor="grey.300"
          px={{ sm: '300' }}
          py={{ sm: '400' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'space-between' }}
          style={{ flex: '1' }}
        >
          <Box style={{ flex: '1' }} gap={{ sm: '300' }} alignItems={{ sm: 'center' }} py={{ sm: '400' }}>
            <Text color="blue.800" weight={600} size={'500'}>
              My Tool Cart
            </Text>
            <ToolCountStyled>
              <Text color="white" size="200">
                {totalItems}
              </Text>
            </ToolCountStyled>
          </Box>
          <Box>
            <Button size={ButtonSize.MEDIUM} onClick={onToggleShowCart}>
              <Icon icon="cart-shopping" size={IconSize.MEDIUM} fillColor="white" />
            </Button>
          </Box>
        </BoxWithBorderStyled>

        <Box flexDirection={{ sm: 'column' }} style={{ height: 'calc(100vh - 160px)', overflowY: 'auto' }}>
          <CartHeaderStyled>
            <CartHeader
              onClearCart={() => {
                toolOrderCatalogItems.forEach((toolOrderCatalogItem) => {
                  onToolCartItemEdit(toolOrderCatalogItem?.toolCatalog, 0);
                  onNonCatalogItemEdit(toolOrderCatalogItem, 0, true);
                });
              }}
              toolOrderCatalogItems={toolOrderCatalogItems}
            ></CartHeader>
          </CartHeaderStyled>
          {toolOrderCatalogItems.length > 0 &&
            toolOrderCatalogItems?.map((toolOrderCatalogItem) => {
              const toolCatalog = toolOrderCatalogItem.toolCatalog;
              if (toolCatalog) {
                return (
                  <BoxWithBorderStyled key={toolOrderCatalogItem.id} borderBottomColor="grey.300">
                    <Box
                      py={{ sm: '500' }}
                      mr={{ sm: '400' }}
                      justifyContent={{ sm: 'space-between' }}
                      style={{ flex: '1' }}
                      alignItems={{ sm: 'center' }}
                    >
                      <Box justifyContent={{ sm: 'center' }} alignItems={{ sm: 'center' }} mx={{ sm: '450' }}>
                        <ImageFallback
                          src={toolCatalog?.toolCatalogImageUrl}
                          fallbackName={toolCatalog?.toolCategory?.name}
                        />
                      </Box>
                      <Box flexDirection={{ sm: 'column' }} style={{ height: '100%', width: '50%' }}>
                        <CatalogInfo
                          barCode={toolCatalog && toolCatalog.bscnPartNumber ? toolCatalog.bscnPartNumber : ''}
                          description={toolCatalog && toolCatalog.name ? toolCatalog.name : ''}
                          isSerialized={
                            ['Individual', 'Equipment'].includes(toolCatalog?.inventoryType ?? '') ? true : false
                          }
                          uomAbbreviation={toolCatalog?.uom ? toolCatalog.uom.abbreviation : ''}
                        />
                        <RemoveCartItemStyled
                          onClick={() => {
                            onToolCartItemRemove(toolOrderCatalogItem?.id, false);
                          }}
                        >
                          <Text color="grey.500" size="300">
                            &#8211; Remove from Cart
                          </Text>
                        </RemoveCartItemStyled>
                      </Box>
                      <Box flexDirection={{ sm: 'column' }} gap={{ sm: '200' }}>
                        <EditCountField
                          count={toolOrderCatalogItem.quantity}
                          onIncrement={() => {
                            onToolCartItemEdit(toolOrderCatalogItem?.toolCatalog, toolOrderCatalogItem.quantity + 1);
                          }}
                          plusBtnDisable={
                            toolCatalog.limitCount &&
                            toolCatalog.limitCount > 0 &&
                            toolOrderCatalogItem.quantity >= toolCatalog?.limitCount
                              ? true
                              : false
                          }
                          onDecrement={() => {
                            if (toolOrderCatalogItem.quantity > 0) {
                              onToolCartItemEdit(toolOrderCatalogItem.toolCatalog, toolOrderCatalogItem.quantity - 1);
                            }
                          }}
                          onCountTextChange={(quantity) => {
                            if (
                              toolCatalog.limitCount &&
                              toolCatalog.limitCount > 0 &&
                              quantity > toolCatalog?.limitCount
                            ) {
                              return;
                            }

                            onToolCartItemEdit(toolOrderCatalogItem.toolCatalog, quantity);
                          }}
                        ></EditCountField>
                        <RestrictedLimitComponent
                          isLocked={toolCatalog?.isLocked}
                          limitCount={toolCatalog?.limitCount}
                        />
                      </Box>
                    </Box>
                  </BoxWithBorderStyled>
                );
              } else {
                return (
                  <BoxWithBorderStyled key={toolOrderCatalogItem.id} borderBottomColor="grey.300">
                    <Box
                      px={{ sm: '400' }}
                      py={{ sm: '500' }}
                      justifyContent={{ sm: 'space-between' }}
                      alignItems={{ sm: 'center' }}
                      gap={{ sm: '500' }}
                    >
                      <Box flexDirection={{ sm: 'column' }} style={{ height: '100%' }}>
                        <NonCatalogInfo
                          nonCatalogDescription={toolOrderCatalogItem.nonCatalogDescription || ''}
                          nonCatalogUomId={toolOrderCatalogItem?.nonCatalogUomId ?? 0}
                        >
                          <NonCatalogImage />
                        </NonCatalogInfo>
                        <Box justifyContent={{ sm: 'center' }} style={{ width: '242px' }}>
                          <RemoveCartItemStyled
                            onClick={() => {
                              onToolCartItemRemove(toolOrderCatalogItem.id, true);
                            }}
                          >
                            <Text color="grey.500" size="300">
                              &#8211; Remove from Cart
                            </Text>
                          </RemoveCartItemStyled>
                        </Box>
                      </Box>
                      <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} gap={{ sm: '400' }}>
                        <EditCountField
                          count={toolOrderCatalogItem.quantity}
                          onIncrement={() => {
                            onNonCatalogItemEdit(toolOrderCatalogItem, toolOrderCatalogItem.quantity + 1, true);
                          }}
                          onDecrement={() => {
                            if (toolOrderCatalogItem.quantity > 0) {
                              onNonCatalogItemEdit(toolOrderCatalogItem, toolOrderCatalogItem.quantity - 1, true);
                            }
                          }}
                          onCountTextChange={(quantity) => {
                            onNonCatalogItemEdit(toolOrderCatalogItem, quantity, true);
                          }}
                        ></EditCountField>
                        <Box>
                          <EditNonCatalogButtonStyled
                            size={ButtonSize.SMALL}
                            onClick={() => {
                              onNonCatalogItemEdit(toolOrderCatalogItem, toolOrderCatalogItem.quantity, false);
                            }}
                          >
                            <ActionContent
                              iconSize={IconSize.XSMALL}
                              labelSize="200"
                              icon="edit"
                              label="Edit"
                            ></ActionContent>
                          </EditNonCatalogButtonStyled>
                        </Box>
                      </Box>
                    </Box>
                  </BoxWithBorderStyled>
                );
              }
            })}
          {!toolOrderCatalogItems?.length && (
            <Box
              px={{ sm: '500' }}
              py={{ sm: '500' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              style={{ flex: '1' }}
              flexDirection={{ sm: 'column' }}
              gap={{ sm: '500' }}
            >
              <EmptyStateBox />
              <Text color="grey.1000" size="400">
                You do not have any item to Reserve.
              </Text>
            </Box>
          )}
        </Box>
        <Box px={{ sm: '500' }} py={{ sm: '300' }} gap={{ sm: '300' }}>
          <Button
            disabled={!totalItems}
            onClick={() => {
              onToolCartSave(true);
            }}
          >
            <ActionContent icon="cart-shopping" label="Continue to cart"></ActionContent>
          </Button>
        </Box>
      </Box>
    </ToolCartStyled>
  );
};
